import React, { useEffect, useMemo, useState } from 'react';
import { View, ScrollView, StyleSheet, Pressable, Platform, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import TopicTagListItem from '@/components/Topics/TopicTagListItem';
import TopicResearchCallout from '@/components/Topics/TopicResearchCallout';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import { topicApiSsr } from '@/lib/topicApi';
import { topicTagApiSsr } from '@/lib/topicTagApi';
import { BORDER_RADIUS } from '@/constants/config';
import { formatCount } from '@/utils/agent_catalog';

const POPULAR_TAGS_LIMIT = 12;

export default function TopicsIndexPage() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, showsScrollIndicator } = useResponsiveness();
  const [categories, setCategories] = useState([]);
  const [popularTags, setPopularTags] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingTags, setLoadingTags] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await topicApiSsr.listCategories();
        const visible = (Array.isArray(list) ? list : []).filter((cat) => (cat?.count ?? 0) > 0);
        if (!cancelled) setCategories(visible);
      } catch {
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await topicTagApiSsr.list({ page: 1, limit: POPULAR_TAGS_LIMIT });
        if (!cancelled) {
          setPopularTags(Array.isArray(res?.topicTags) ? res.topicTags : []);
        }
      } catch {
        if (!cancelled) setPopularTags([]);
      } finally {
        if (!cancelled) setLoadingTags(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const styles = useMemo(() => StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.solid_color_primary },
    scroll: { flex: 1 },
    inner: {
      paddingHorizontal: isXS ? 16 : 24,
      paddingTop: 16,
      paddingBottom: 32,
      maxWidth: 900,
      alignSelf: 'center',
      width: '100%',
      gap: 16,
    },
    section: { gap: 10 },
    sectionTitle: { marginBottom: 2 },
    card: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      padding: 16,
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      ...(Platform.OS === 'web' ? { cursor: 'pointer' } : null),
    },
    loading: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, paddingVertical: 24 },
    viewAllTags: { alignSelf: 'flex-start', marginTop: 4 },
  }), [colors, isXS]);

  return (
    <View style={styles.container}>
      <MetaHead
        title={`${t('pages.topics.title')} · Gab AI`}
        description={t('pages.topics.seoMetaFallback')}
        canonical="/topics"
      />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={showsScrollIndicator}
      >
        <View style={styles.inner}>
          <View>
            <Text type="h2">{t('pages.topics.title')}</Text>
            <Text type="p" style={{ opacity: 0.8 }}>{t('pages.topics.seoMetaFallback')}</Text>
          </View>

          <TopicResearchCallout />

          <View style={styles.section}>
            <Text type="h3" style={styles.sectionTitle}>{t('pages.topics.categoriesTitle')}</Text>
            {loadingCategories ? (
              <View style={styles.loading}>
                <ActivityIndicator color={colors.text_color_secondary} />
              </View>
            ) : (
              categories.map((cat) => (
                <Pressable
                  key={cat.slug}
                  style={styles.card}
                  onPress={() => router.push(`/topics/category/${encodeURIComponent(cat.slug)}`)}
                  accessibilityRole="button"
                >
                  <Icon name="book" size={18} color={colors.text_color_secondary} />
                  <View style={{ flex: 1, gap: 2 }}>
                    <Text type="p" weight="bold">{cat.name}</Text>
                    {cat.count > 0 && (
                      <Text size="xs" color="tertiary">
                        {formatCount(cat.count)} {cat.count === 1 ? 'topic' : 'topics'}
                      </Text>
                    )}
                  </View>
                  <Icon name="arrow-right" size={14} color={colors.text_color_tertiary} />
                </Pressable>
              ))
            )}
          </View>

          <View style={styles.section}>
            <Text type="h3" style={styles.sectionTitle}>{t('pages.topics.popularTags')}</Text>
            {loadingTags ? (
              <View style={styles.loading}>
                <ActivityIndicator color={colors.text_color_secondary} />
              </View>
            ) : (
              <>
                <View style={{ gap: 6 }}>
                  {popularTags.map((tag) => (
                    <TopicTagListItem
                      key={tag.id}
                      tag={tag}
                      colors={colors}
                      onPress={() => tag.slug && router.push(`/tags/${encodeURIComponent(tag.slug)}`)}
                    />
                  ))}
                </View>
                {popularTags.length > 0 && (
                  <Button
                    text={t('nav.routes.topicTags.viewAllTags')}
                    variant="secondary"
                    size="small"
                    prependIcon="tag"
                    onPress={() => router.push('/tags')}
                    style={styles.viewAllTags}
                  />
                )}
              </>
            )}
          </View>
        </View>
        <View style={{ paddingHorizontal: isXS ? 16 : 24, paddingTop: 8 }}>
          <AppDashboardFooter />
        </View>
      </ScrollView>
    </View>
  );
}
